import { combineValidateRules } from 'legacy/lut/helpers/combineValidateRules'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { downloadBlob } from 'clickNgo/helpers/common'
import { Button } from 'core-components/library/button'
import { Dropdown } from 'core-components/library/dropdown'
import { Table } from 'core-components/library/list'
import {
  Controller,
  IControllerResult,
  ValidateInput,
  ValidationStatus,
} from 'core-components/library/validation'
import { getTokens } from 'core-components/sitebuilder/helpers/getTokens'
import { selectAppOwnerId } from 'core-components/sitebuilder/store/selectors/selectAppOwnerId'
import { IDataProviderFilter, Static } from 'core-components/types'
import { Crud } from 'core/types/Crud'
import Logger from 'core/utils/Logger'
import { showError } from 'onboarding/classes/services/notification'
import { APLAMSTranslationCodes } from 'onboarding/configs/translations/aplAMSTranslationCodes'
import { LanguageContext } from 'onboarding/context/LanguageContext'
import { aplService } from 'onboarding/services/aplService'
import { findFilterItem } from 'onboarding/utils/filter'
import { REGEX_COMPANY_INVALID_CHARACTERS } from 'src/config/regexp'

import { TABLE_PAGINATION_OPTIONS } from './configs'
import { getTableColumns } from './helpers/getTableColumns'
import { useAmsLatamDataCrud } from './hooks/useAmsLatamDataCrud'

import styles from './PageContent.module.scss'

const COUNTRIES_SOURCE = new Crud({
  fetcher: new Static([
    { id: 'ar', value: 'Argentina' },
    { id: 'co', value: 'Colombia' },
    { id: 'mx', value: 'Mexico' },
    { id: 'pe', value: 'Peru' },
    { id: 'rcs', value: 'RCS' },
  ]),
  keyField: 'id',
})

export const PageContent = () => {
  const appOwnerId = useSelector(selectAppOwnerId)
  const { t } = useContext(LanguageContext)

  const [canSearch, setCanSearch] = useState(false)
  const [isRequiredValidation, setIsRequiredValidation] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const [companyName, setCompanyName] = useState('')
  const [country, setCountry] = useState('')
  const [taxId, setTaxId] = useState('')

  const columns = getTableColumns(t)
  const { crud, applySearchFilter } = useAmsLatamDataCrud({
    companyName: companyName,
    countryISO: country,
    taxId: taxId,
  })

  const valueDependencies = useMemo(() => {
    return [country, taxId, companyName]
  }, [country, taxId, companyName])

  const handleValidationStatusChange = useCallback((result: IControllerResult) => {
    if (result.status === ValidationStatus.Valid) {
      setCanSearch(true)
    } else if (result.status === ValidationStatus.Invalid) {
      setCanSearch(false)
    }
  }, [])

  const setValidationToRequired = useCallback(() => {
    setIsRequiredValidation(true)
  }, [])

  const prepareErrorMessageFromMatched = useCallback((matched: RegExpMatchArray) => {
    if (!matched) {
      return ''
    }

    const charsInMatches = matched.reduce((previousValue: string[], group: string) => {
      return [...previousValue, ...Array.from(group)]
    }, [])

    const set = new Set(charsInMatches)
    return `Should not contain: ${Array.from(set).join(' ')}`
  }, [])

  const checkCompanyName = useCallback(
    async (value: string) => {
      if (!isRequiredValidation) {
        return ''
      }

      if (!taxId && country && !value) {
        return 'Required'
      }

      if (value && value.trim().length < 3) {
        return t(APLAMSTranslationCodes.search_company_name_requirements)
      }

      const matched = value.match(REGEX_COMPANY_INVALID_CHARACTERS)
      if (matched) {
        return prepareErrorMessageFromMatched(matched)
      }

      return ''
    },
    [isRequiredValidation, taxId, country, prepareErrorMessageFromMatched]
  )

  const isOneOfRequired = useCallback(async () => {
    if (!isRequiredValidation) {
      return ''
    }

    if (!companyName && !country && !taxId) {
      return 'Required: Company Name and Country or Tax ID'
    }

    return ''
  }, [isRequiredValidation, companyName, taxId, country])

  const checkCountry = useCallback(
    async (value: string) => {
      if (!isRequiredValidation) {
        return ''
      }

      if (!taxId && companyName && !value) {
        return 'Required'
      }

      return ''
    },
    [isRequiredValidation, taxId, companyName]
  )

  const checkTaxID = useCallback(
    async (value: string) => {
      if (!isRequiredValidation) {
        return ''
      }

      const trimmed = value.trim()

      if (!trimmed && ((companyName && !country) || (!companyName && country))) {
        return 'Required'
      }

      return ''
    },
    [companyName, country, isRequiredValidation]
  )

  const exportEligiblePartners = async () => {
    const [accessToken] = getTokens(appOwnerId)
    if (!accessToken) {
      showError('Please, re-log in to the application')

      return
    }
    const filter = crud.getFilter()
    if (!filter?.filters.length) {
      showError('Only search result can be exported. Please, apply search filters first.')

      return
    }

    const extractFilterValue = (filter: IDataProviderFilter, field: string) =>
      (findFilterItem(filter, field)?.value as string) || undefined
    try {
      setIsExporting(true)
      const { fileName, file } = await aplService.exportAmsLatamEligiblePartners(
        {
          communicationName: extractFilterValue(filter, 'companyName'),
          countryISO: extractFilterValue(filter, 'countryISO'),
          taxId: extractFilterValue(filter, 'taxId'),
        },
        accessToken
      )
      downloadBlob(fileName, file)
    } catch (e: any) {
      Logger.error(e)
      showError(e.message)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className={styles.content}>
      <div>
        Please search for HP Authorized Partners by using either the company name and country or the
        company Tax ID
      </div>
      <div className={styles.searchArea}>
        <Controller onValidationStatusChange={handleValidationStatusChange}>
          <ValidateInput
            onKeyDown={setValidationToRequired}
            valueDependencies={valueDependencies}
            classes={{ validatorClasses: { root: styles.searchArea__input } }}
            value={companyName}
            onChange={setCompanyName}
            label={t(APLAMSTranslationCodes.company_name_filter)}
            checkFn={combineValidateRules([checkCompanyName, isOneOfRequired])}
          />
          <Dropdown
            valueDependencies={valueDependencies}
            checkFn={combineValidateRules([checkCountry, isOneOfRequired])}
            onBlur={() => {
              setIsRequiredValidation(true)
            }}
            classes={{ controlClasses: { root: styles.searchArea__input } }}
            source={COUNTRIES_SOURCE}
            value={country}
            caption={'value'}
            onChange={setCountry}
            label={t(APLAMSTranslationCodes.country_filter)}
          />
          <ValidateInput
            valueDependencies={valueDependencies}
            onKeyDown={() => {
              setIsRequiredValidation(true)
            }}
            classes={{ validatorClasses: { root: styles.searchArea__input } }}
            value={taxId}
            onChange={setTaxId}
            label={t(APLAMSTranslationCodes.tax_id_filter)}
            checkFn={combineValidateRules([checkTaxID, isOneOfRequired])}
          />
          <Button
            className={styles.searchArea__button}
            onClick={applySearchFilter}
            disabled={!canSearch || !isRequiredValidation}
          >
            {t(APLAMSTranslationCodes.search_button)}
          </Button>
        </Controller>
      </div>

      <Table
        header
        isHeaderSticky
        hasExport={false}
        pagination
        rowsPerPageOptions={TABLE_PAGINATION_OPTIONS}
        source={crud}
        columns={columns}
        hasRefresh
      />
      <div className={styles.export}>
        <Button onClick={exportEligiblePartners} loading={isExporting}>
          Export
        </Button>
      </div>
    </div>
  )
}
